import React, { useMemo, useState } from 'react';
import type { AiModelConfig, AiModelOption, AiPromptTemplate, AiTestProject } from './types';
import { createTesterRun } from './api';
import ModelPicker from './ModelPicker';
import PromptPicker from './PromptPicker';

interface SourceOfTruthFormProps {
  projects: AiTestProject[];
  models: AiModelOption[];
  prompts: AiPromptTemplate[];
  onPromptsChanged: () => Promise<void> | void;
  onRunStarted: () => Promise<void> | void;
}

const SourceOfTruthForm: React.FC<SourceOfTruthFormProps> = ({
  projects,
  models,
  prompts,
  onPromptsChanged,
  onRunStarted,
}) => {
  const [projectId, setProjectId] = useState<number | ''>('');
  const [useBrandGuidelines] = useState(false);
  const [useOpportunitySpace, setUseOpportunitySpace] = useState(true);
  const [useMarkets, setUseMarkets] = useState(true);
  const [modelId, setModelId] = useState('');
  const [modelConfig, setModelConfig] = useState<AiModelConfig>({
    web_search: true,
  });
  const [prompt, setPrompt] = useState<AiPromptTemplate | null>(null);
  const [promptContent, setPromptContent] = useState('');
  const [synthesisPrompt, setSynthesisPrompt] = useState<AiPromptTemplate | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const project = projects.find((p) => p.id === projectId);
  const marketCount = project?.markets?.length ?? 0;
  const isMultiMarket = marketCount > 1;
  const sotPrompts = useMemo(
    () => prompts.filter((p) => !p.step_key || p.step_key === ''),
    [prompts]
  );
  const synthesisPrompts = useMemo(
    () => prompts.filter((p) => p.step_key === 'market_synthesis'),
    [prompts]
  );

  const handleRun = async () => {
    if (!project) {
      setError('Select a project first.');
      return;
    }
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    const variableKeys: string[] = [];
    if (useOpportunitySpace) variableKeys.push('opportunity_space');
    if (useMarkets) variableKeys.push('markets');
    try {
      const inline = promptContent.trim();
      const run = await createTesterRun({
        project_id: project.id,
        generator_key: 'source_of_truth',
        variable_keys: variableKeys,
        model_id: modelId || undefined,
        model_config: modelConfig,
        prompt_inline: inline || undefined,
        prompt_id: inline ? undefined : prompt?.id || undefined,
        blob_filename: inline || prompt?.id ? undefined : prompt?.blob_filename || undefined,
        synthesis_prompt_id: synthesisPrompt?.id || undefined,
        synthesis_blob_filename: synthesisPrompt?.blob_filename || undefined,
        countries: useMarkets ? project.markets : undefined,
      });
      setSuccess(`Run #${run.id} started (${run.status}).`);
      await onRunStarted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start the run');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <label className="block text-[12px] font-medium text-[#626262] mb-1">Project</label>
        <select
          value={projectId}
          onChange={(e) => setProjectId(e.target.value ? Number(e.target.value) : '')}
          className="w-full rounded-md border border-[#D1D1D1] px-3 py-2 text-sm"
        >
          <option value="">Select a project</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <p className="text-[12px] font-medium text-[#626262] mb-2">Prompt variables</p>
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm text-[#8A8A8A]">
            <input type="checkbox" checked={useBrandGuidelines} disabled />
            Brand Guidelines (not wired yet)
          </label>
          <label className="flex items-center gap-2 text-sm text-[#4A4A4A]">
            <input
              type="checkbox"
              checked={useOpportunitySpace}
              onChange={(e) => setUseOpportunitySpace(e.target.checked)}
            />
            Opportunity Space
          </label>
          <label className="flex items-center gap-2 text-sm text-[#4A4A4A]">
            <input
              type="checkbox"
              checked={useMarkets}
              onChange={(e) => setUseMarkets(e.target.checked)}
            />
            Market/s
            {project && (
              <span className="text-[12px] text-[#8A8A8A]">
                ({project.markets.join(', ') || 'none'})
              </span>
            )}
          </label>
        </div>
      </div>

      <ModelPicker
        models={models}
        modelId={modelId}
        onModelId={setModelId}
        config={modelConfig}
        onConfig={setModelConfig}
        generatorKey="source_of_truth"
        disabled={submitting}
      />

      <PromptPicker
        label={isMultiMarket ? 'Per-market source of truth prompt' : 'Source of truth prompt'}
        generatorKey="source_of_truth"
        stepKey=""
        prompts={sotPrompts}
        selectedId={prompt?.id ?? null}
        blobFilename={prompt?.blob_filename || ''}
        onSelect={setPrompt}
        onContentChange={setPromptContent}
        onPromptsChanged={onPromptsChanged}
        disabled={submitting}
      />

      {isMultiMarket && (
        <PromptPicker
          label="Market synthesis prompt"
          generatorKey="source_of_truth"
          stepKey="market_synthesis"
          prompts={synthesisPrompts}
          selectedId={synthesisPrompt?.id ?? null}
          blobFilename={synthesisPrompt?.blob_filename || ''}
          onSelect={setSynthesisPrompt}
          onPromptsChanged={onPromptsChanged}
          disabled={submitting}
        />
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}
      {success && <p className="text-sm text-emerald-700">{success}</p>}

      <button
        type="button"
        onClick={() => void handleRun()}
        disabled={submitting || !project}
        className="h-9 px-4 rounded-md bg-[#3da3d4] text-white text-sm font-medium disabled:opacity-50"
      >
        {submitting ? 'Starting…' : 'Run source of truth'}
      </button>
    </div>
  );
};

export default SourceOfTruthForm;
